/**
 * Connect Channel View
 * 
 * Handles channel connection consent and API calls.
 * Now uses module's own hooks and services.
 */

import { FC, useState, useEffect, useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ToastMessageContext } from '@context';
import { 
  useConnectChannelMutation, 
  useConnectAirbnbMutation,
  useGathernRequestOTPMutation,
  useGathernVerifyOTPMutation,
  useCheckBookingConnectionMutation,
  useValidateBookingHotelIdsMutation,
  useValidateBookingSingleUnitIdMutation,
  useImportBookingSingleListingMutation,
} from '../../hooks';
import { ChannelName, BookingUnitType, IHotelValidationResult } from '../../types';
import { T_KEYS } from '../../constants';
import { 
  ConnectStep, 
  GathernPhoneStep, 
  GathernOTPStep, 
  BookingUnitTypeStep, 
  BookingPropertyIdStep 
} from '../../components';
import {
  ConnectPageContainer,
  PageTitle,
  PageDescription,
  BackArrowButton,
  HeaderWithBack,
  HeaderContent,
} from '../../styles';

// Gathern flow steps
type GathernStep = 'consent' | 'phone' | 'otp';

// Booking flow steps
type BookingStep = 'consent' | 'unitType' | 'propertyId';

const ConnectChannelView: FC = () => {
  const navigate = useNavigate();
  const { integration } = useParams<{ integration: string }>();
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const isRtl = language === 'ar';
  const { showSuccess, showError } = useContext(ToastMessageContext);

  // Validate channel name
  const channelName = integration as ChannelName;
  const validChannels: ChannelName[] = ['airbnb', 'booking', 'gathern'];
  
  useEffect(() => {
    if (!validChannels.includes(channelName)) {
      navigate('/channels');
    }
  }, [channelName, navigate]);

  // Loading state
  const [isLoading, setIsLoading] = useState(false);

  // Gathern-specific state
  const [gathernStep, setGathernStep] = useState<GathernStep>('consent');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneError, setPhoneError] = useState<string | undefined>();
  const [otpError, setOtpError] = useState<string | undefined>();

  // Booking-specific state
  const [bookingStep, setBookingStep] = useState<BookingStep>('consent');
  const [bookingUnitType, setBookingUnitType] = useState<BookingUnitType>('single');
  const [bookingError, setBookingError] = useState<string | undefined>();
  const [bookingValidationResults, setBookingValidationResults] = useState<IHotelValidationResult[] | undefined>();
  const [hasBookingValidationErrors, setHasBookingValidationErrors] = useState(false);

  // Mutations from module hooks
  const { mutate: connectAirbnb } = useConnectAirbnbMutation();
  const { mutate: mockConnectChannel } = useConnectChannelMutation();
  const { mutate: requestOTP, isPending: isRequestingOTP } = useGathernRequestOTPMutation();
  const { mutate: verifyOTP, isPending: isVerifyingOTP } = useGathernVerifyOTPMutation();
  const { mutate: checkBookingConnection, isPending: isCheckingBooking } = useCheckBookingConnectionMutation();
  const { mutate: validateBookingHotelIds, isPending: isValidatingBookingIds } = useValidateBookingHotelIdsMutation();
  const { mutateAsync: validateSingleUnitId } = useValidateBookingSingleUnitIdMutation();
  const { mutate: importBookingSingleListing, isPending: isImportingSingleListing } = useImportBookingSingleListingMutation();

  // Handle Airbnb connection
  const handleAirbnbConnect = useCallback(() => {
    setIsLoading(true);

    connectAirbnb(undefined, {
      onSuccess: (data) => {
        if (data.connectionLink) {
          window.location.href = data.connectionLink;
        } else {
          showError(t(T_KEYS.FAILED_GET_CONNECTION_LINK));
          setIsLoading(false);
        }
      },
      onError: (error) => {
        showError(error?.message || t('ERRORS.SOMETHING_WENT_WRONG'));
        setIsLoading(false);
      },
    });
  }, [connectAirbnb, showError, isRtl, t]);

  // Handle Gathern consent
  const handleGathernConsent = useCallback(() => {
    setGathernStep('phone');
  }, []);

  // Handle back navigation
  const handleBack = useCallback(() => {
    if (channelName === 'gathern') {
      if (gathernStep === 'otp') {
        setGathernStep('phone');
        setOtpError(undefined);
        return;
      }
      if (gathernStep === 'phone') {
        setGathernStep('consent');
        setPhoneError(undefined);
        return;
      }
    }

    if (channelName === 'booking') {
      if (bookingStep === 'propertyId') {
        setBookingStep('unitType');
        setBookingError(undefined);
        setBookingValidationResults(undefined);
        setHasBookingValidationErrors(false);
        return;
      }
      if (bookingStep === 'unitType') {
        setBookingStep('consent');
        return;
      }
    }

    navigate(-1);
  }, [channelName, gathernStep, bookingStep, navigate]);

  // Handle Booking consent
  const handleBookingConsent = useCallback(() => {
    setBookingStep('unitType');
  }, []);

  // Handle Booking unit type selection
  const handleBookingUnitTypeSelect = useCallback((unitType: BookingUnitType) => {
    setBookingUnitType(unitType);
    setBookingStep('propertyId');
  }, []);

  // Handle Booking property IDs submission
  const handleBookingPropertyIdsSubmit = useCallback((propertyIds: string[]) => {
    setBookingError(undefined);
    setBookingValidationResults(undefined);
    setHasBookingValidationErrors(false);

    validateBookingHotelIds(
      { hotelIds: propertyIds, mainChannelId: null },
      {
        onSuccess: (data) => {
          if (data.invalidCount > 0) {
            setBookingValidationResults(data.results ?? undefined);
            setHasBookingValidationErrors(true);
            showError(t(T_KEYS.SOME_PROPERTY_IDS_INVALID));
          } else {
            showSuccess(t(T_KEYS.VALIDATION_SUCCESSFUL));
            const hotelId = data.hotelId;
            navigate(`/channels/booking/units/import?channelId=${hotelId}&newConnection=true`);
          }
        },
        onError: (error) => {
          const errorWithAr = error as Error & { messageAr?: string };
          const errorMsg = isRtl 
            ? (errorWithAr.messageAr || errorWithAr.message) 
            : errorWithAr.message;
          setBookingError(errorMsg || t('ERRORS.SOMETHING_WENT_WRONG'));
        },
      }
    );
  }, [validateBookingHotelIds, showSuccess, showError, isRtl, navigate, t]);

  // Handle Booking single property ID submission
  const handleBookingSinglePropertyIdSubmit = useCallback((propertyId: string) => {
    setBookingError(undefined);

    checkBookingConnection(
      { hotelId: propertyId, connectionType: bookingUnitType === 'single' ? 1 : 2 },
      {
        onSuccess: (data) => {
          showSuccess(isRtl ? data.messageAr : data.message);
          const hotelId = data.hotelId;
          navigate(`/channels/booking/units/import?channelId=${hotelId}&newConnection=true`);
        },
        onError: (error) => {
          const errorWithAr = error as Error & { messageAr?: string };
          const errorMsg = isRtl 
            ? (errorWithAr.messageAr || errorWithAr.message) 
            : errorWithAr.message;
          const msg = errorMsg || t('ERRORS.SOMETHING_WENT_WRONG');
          setBookingError(msg);
          showError(msg);
        },
      }
    );
  }, [checkBookingConnection, bookingUnitType, showSuccess, showError, isRtl, navigate, t]);

  // Handle single property ID verification on blur (uses new ValidateBookingSingleUnitId API)
  const handleVerifySinglePropertyId = useCallback(async (propertyId: string): Promise<{ isValid: boolean; error?: string; channelId?: string }> => {
    try {
      const data = await validateSingleUnitId({ hotelId: propertyId });
      
      if (data.invalidCount > 0) {
        const result = data.results?.find(r => r.hotelId === propertyId);
        const errorMsg = isRtl 
          ? (result?.errorMessageAr || result?.errorMessageEn || t(T_KEYS.INVALID_ID_ERROR))
          : (result?.errorMessageEn || result?.errorMessageAr || t(T_KEYS.INVALID_ID_ERROR));
        return { isValid: false, error: errorMsg };
      }
      
      return { isValid: true, channelId: data.hotelId ?? undefined };
    } catch (error) {
      const errorWithAr = error as Error & { messageAr?: string };
      const errorMsg = isRtl 
        ? (errorWithAr.messageAr || errorWithAr.message) 
        : errorWithAr.message;
      return { isValid: false, error: errorMsg || t(T_KEYS.VERIFICATION_FAILED) };
    }
  }, [validateSingleUnitId, isRtl, t]);

  // Handle submit for 'single' unitType - import all verified hotel IDs
  const handleImportSingleListings = useCallback((hotelIds: string[]) => {
    if (hotelIds.length === 0) return;

    importBookingSingleListing(
      { hotelIds, mainChannelId: null },
      {
        onSuccess: (data) => {
          console.log('[ImportSingleListing] Full response data:', JSON.stringify(data));
          console.log('[ImportSingleListing] userChannelId:', data.userChannelId);
          const successMsg = isRtl ? (data.messageAr || data.message) : (data.message || data.messageAr);
          showSuccess(successMsg || t(T_KEYS.IMPORT_SUCCESS));
          // Navigate to the account page if we have the userChannelId from import response
          if (data.userChannelId) {
            navigate(`/channels/booking/manage?accountId=${data.userChannelId}`);
          } else {
            navigate('/channels');
          }
        },
        onError: (error) => {
          const errorWithAr = error as Error & { messageAr?: string };
          const errorMsg = isRtl 
            ? (errorWithAr.messageAr || errorWithAr.message) 
            : errorWithAr.message;
          const msg = errorMsg || t('ERRORS.SOMETHING_WENT_WRONG');
          setBookingError(msg);
          showError(msg);
        },
      }
    );
  }, [importBookingSingleListing, showSuccess, showError, isRtl, navigate, t]);

  // Handle Gathern phone submission
  const handleGathernPhoneSubmit = useCallback((phone: string) => {
    setPhoneError(undefined);
    setPhoneNumber(phone);

    requestOTP(
      { phoneNumber: phone },
      {
        onSuccess: (data) => {
          setGathernStep('otp');
          showSuccess(isRtl ? data.messageAr : data.message);
        },
        onError: (error) => {
          const errorMsg = error?.message || t('ERRORS.SOMETHING_WENT_WRONG');
          setPhoneError(errorMsg);
        },
      }
    );
  }, [requestOTP, showSuccess, isRtl, t]);

  // Handle Gathern OTP verification
  const handleGathernOTPSubmit = useCallback((otp: string) => {
    setOtpError(undefined);

    verifyOTP(
      { phoneNumber, otp },
      {
        onSuccess: (data) => {
          showSuccess(isRtl ? data.messageAr : data.message);
          const channelId = data.token;
          navigate(`/channels/gathern/units/import?channelId=${channelId}&newConnection=true`);
        },
        onError: (error) => {
          const errorWithAr = error as Error & { messageAr?: string };
          const errorMsg = isRtl 
            ? (errorWithAr.messageAr || errorWithAr.message) 
            : errorWithAr.message;
          setOtpError(errorMsg || t('ERRORS.SOMETHING_WENT_WRONG'));
        },
      }
    );
  }, [verifyOTP, phoneNumber, showSuccess, isRtl, navigate, t]);

  // Handle Gathern OTP resend
  const handleGathernOTPResend = useCallback(() => {
    setOtpError(undefined);

    requestOTP(
      { phoneNumber },
      {
        onSuccess: (data) => {
          showSuccess(isRtl ? data.messageAr : data.message);
        },
        onError: (error) => {
          const errorMsg = error?.message || t('ERRORS.SOMETHING_WENT_WRONG');
          showError(errorMsg);
        },
      }
    );
  }, [requestOTP, phoneNumber, showSuccess, showError, isRtl, t]);

  // Handle other channels connection
  const handleOtherChannelConnect = useCallback(() => {
    setIsLoading(true);

    mockConnectChannel(
      { channelName },
      {
        onSuccess: () => {
          showSuccess(t('CHANNELS.CONNECTION_SUCCESS'));
          navigate(`/channels/${channelName}/units/import?newConnection=true`);
        },
        onError: (error) => {
          showError(error?.message || t('ERRORS.SOMETHING_WENT_WRONG'));
          setIsLoading(false);
        },
      }
    );
  }, [channelName, mockConnectChannel, showSuccess, showError, t, navigate]);

  // Handle connect button
  const handleConnect = useCallback(() => {
    if (channelName === 'airbnb') {
      handleAirbnbConnect();
    } else if (channelName === 'gathern') {
      handleGathernConsent();
    } else if (channelName === 'booking') {
      handleBookingConsent();
    } else {
      handleOtherChannelConnect();
    }
  }, [channelName, handleAirbnbConnect, handleGathernConsent, handleBookingConsent, handleOtherChannelConnect]);

  // Handle cancel
  const handleCancel = useCallback(() => {
    navigate('/channels');
  }, [navigate]);

  // Get page title
  const getPageTitle = () => {
    if (channelName === 'booking' && bookingStep !== 'consent') {
      return t(T_KEYS.CONNECT_BOOKING_ACCOUNT);
    }
    const channelDisplayName = channelName === 'gathern' && isRtl ? 'Gathern' : 
      channelName.charAt(0).toUpperCase() + channelName.slice(1);
    return t(T_KEYS.CONNECT_CHANNEL_ACCOUNT, { channel: channelDisplayName });
  };

  const getPageDescription = () => {
    if (channelName === 'booking' && bookingStep !== 'consent') {
      return t(T_KEYS.CONNECT_DESCRIPTION_BOOKING);
    }
    return t(T_KEYS.CONNECT_DESCRIPTION_DEFAULT);
  };

  // Render Booking-specific steps
  const renderBookingContent = () => {
    switch (bookingStep) {
      case 'unitType':
        return (
          <BookingUnitTypeStep
            onSelect={handleBookingUnitTypeSelect}
            onBack={() => setBookingStep('consent')}
          />
        );
      case 'propertyId':
        return (
          <BookingPropertyIdStep
            unitType={bookingUnitType}
            onSubmit={handleBookingPropertyIdsSubmit}
            onSubmitSingle={handleBookingSinglePropertyIdSubmit}
            onVerifySingle={handleVerifySinglePropertyId}
            onAutoImport={handleImportSingleListings}
            isLoading={bookingUnitType === 'single' ? (isValidatingBookingIds || isImportingSingleListing) : isCheckingBooking}
            error={bookingError}
            validationResults={bookingValidationResults}
            hasValidationErrors={hasBookingValidationErrors}
          />
        );
      default:
        return (
          <ConnectStep
            channelName={channelName}
            onCancel={handleCancel}
            onConnect={handleConnect}
            isLoading={isLoading}
          />
        );
    }
  };

  // Render Gathern-specific steps
  const renderGathernContent = () => {
    switch (gathernStep) {
      case 'phone':
        return (
          <GathernPhoneStep
            onSubmit={handleGathernPhoneSubmit}
            isLoading={isRequestingOTP}
            error={phoneError}
          />
        );
      case 'otp':
        return (
          <GathernOTPStep
            phoneNumber={phoneNumber}
            onSubmit={handleGathernOTPSubmit}
            onResend={handleGathernOTPResend}
            isLoading={isVerifyingOTP}
            error={otpError}
          />
        );
      default:
        return (
          <ConnectStep
            channelName={channelName}
            onCancel={handleCancel}
            onConnect={handleConnect}
            isLoading={isLoading}
          />
        );
    }
  };

  const showPageHeader = !(channelName === 'booking' && bookingStep === 'unitType');

  const ArrowIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M19 12H5M12 19l-7-7 7-7" />
    </svg>
  );

  return (
    <ConnectPageContainer>
      {showPageHeader && (
        <HeaderWithBack>
          <BackArrowButton onClick={handleBack} aria-label={t(T_KEYS.BACK)}>
            <ArrowIcon />
          </BackArrowButton>
          <HeaderContent>
            <PageTitle>{getPageTitle()}</PageTitle>
            <PageDescription>{getPageDescription()}</PageDescription>
          </HeaderContent>
        </HeaderWithBack>
      )}

      {channelName === 'gathern' ? (
        renderGathernContent()
      ) : channelName === 'booking' ? (
        renderBookingContent()
      ) : (
        <ConnectStep
          channelName={channelName}
          onCancel={handleCancel}
          onConnect={handleConnect}
          isLoading={isLoading}
        />
      )}
    </ConnectPageContainer>
  );
};

export default ConnectChannelView;
